import { type Employee, type UserApprover } from "@finway-group/shared/lib/models"
import { type ActionCreator, type Dispatch } from "redux"
import { EmployeeService, UserService } from "~Shared/services"
import { type ThunkResult } from "~Shared/store"
import {
    EmployeeActionTypes,
    type FetchAllEmployeesAction,
    type NewUserAction,
    type SyncEmployeeAction,
    type UpdateEmployeeAction,
    type UpdateUserAction,
} from "~Shared/store/actions/employee/employeeTypes"
import { type CancelFetchAction, FetcherActionTypes } from "../fetcher/fetcherTypes"
import { refetchUserRelatedTables } from "../tables/tableActions"

export const fetchAllEmployees: ActionCreator<ThunkResult<Array<Employee>>> = () => async (dispatch: Dispatch) => {
    const employees = await EmployeeService.fetchAllEmployees()
    const cancelFetchAction: CancelFetchAction = {
        type: FetcherActionTypes.CANCEL_FETCH,
        isFetchCancelled: true,
    }

    dispatch(cancelFetchAction)

    const fetchAllEmployeesAction: FetchAllEmployeesAction = {
        type: EmployeeActionTypes.FETCH_ALL_EMPLOYEES,
        employees,
    }

    dispatch(fetchAllEmployeesAction)

    return employees
}

export const syncEmployee = (id: string) => async (dispatch: Dispatch) => {
    const employee = await EmployeeService.fetchEmployeeById(id)

    if (employee) {
        const syncEmployeeAction: SyncEmployeeAction = {
            type: EmployeeActionTypes.SYNC_EMPLOYEE,
            employee,
        }

        dispatch(syncEmployeeAction)
    }

    return employee
}

export const syncEmployeeWithEmployeeInfo = (employee: Employee) => async (dispatch: Dispatch) => {
    const syncEmployeeAction: SyncEmployeeAction = {
        type: EmployeeActionTypes.SYNC_EMPLOYEE,
        employee,
    }

    dispatch(syncEmployeeAction)

    return employee
}

export const createUser: ActionCreator<ThunkResult<Employee>> = (createEmployee: Employee) => async (dispatch: Dispatch) => {
    const employee = await EmployeeService.createEmployee(createEmployee)
    const newEmployeeAction: NewUserAction = {
        type: EmployeeActionTypes.NEW_USER,
        employee,
    }

    dispatch(newEmployeeAction)
    dispatch(refetchUserRelatedTables())

    return employee
}

export const updateUser = (id: string, updateEmployee: Employee) => async (dispatch: Dispatch) => {
    const employee = await UserService.update(id, updateEmployee)
    const updateUserAction: UpdateUserAction = {
        type: EmployeeActionTypes.UPDATE_USER,
        employee,
    }

    dispatch(updateUserAction)
    dispatch(refetchUserRelatedTables())

    return employee
}

export const updateEmployee = (id: string, updateEmployee: Employee) => async (dispatch: Dispatch) => {
    const employee = await EmployeeService.updateEmployee(id, updateEmployee)
    const updateEmployeeAction: UpdateEmployeeAction = {
        type: EmployeeActionTypes.UPDATE_EMPLOYEE,
        employee,
    }

    dispatch(updateEmployeeAction)
    dispatch(refetchUserRelatedTables())

    return employee
}

export const patchCompanyProfile = (userId: string, companyId: string, payload: { creditorNumber: string }) => async (dispatch: Dispatch) => {
    const employee = await UserService.patchCompanyProfile({ userId, companyId, payload })
    await syncEmployeeWithEmployeeInfo(employee)(dispatch)
    return employee
}

export const updateEmployeeSettings =
    (id: string, { settings }: Employee) =>
    async (dispatch: Dispatch) => {
        const employee = await EmployeeService.updateEmployeeSettings(id, settings)
        const updateSettingsAction: UpdateEmployeeAction = {
            type: EmployeeActionTypes.UPDATE_EMPLOYEE,
            employee,
        }

        dispatch(updateSettingsAction)

        return employee
    }

export const updateEmployeeImg = (id: string, imgUrl: string) => async (dispatch: Dispatch) => {
    const employee = await EmployeeService.updateEmployeeImg(id, imgUrl)
    const updateImgAction: UpdateEmployeeAction = {
        type: EmployeeActionTypes.UPDATE_EMPLOYEE,
        employee,
    }

    dispatch(updateImgAction)
    dispatch(refetchUserRelatedTables())

    return employee
}

export const updateEmployeeAbsence = (id: string, absence: any) => async (dispatch: Dispatch) => {
    const employee = await EmployeeService.updateAbsence(id, absence)
    const updateAbsenceAction: UpdateEmployeeAction = {
        type: EmployeeActionTypes.UPDATE_EMPLOYEE,
        employee,
    }

    dispatch(updateAbsenceAction)

    return employee
}

export const updateEmployeeRule = (id: string, rule: any) => async (dispatch: Dispatch) => {
    const employee = await EmployeeService.updateEmployeeRule(id, rule)
    const updateRuleAction: UpdateEmployeeAction = {
        type: EmployeeActionTypes.UPDATE_EMPLOYEE,
        employee,
    }
    dispatch(updateRuleAction)
    dispatch(refetchUserRelatedTables())
    return employee
}

export const updateEmployeeApprovals = (id: string, approvals: Array<UserApprover>, companyId: string) => async (dispatch: Dispatch) => {
    const employee = await EmployeeService.updateEmployeeApprovals(id, approvals, companyId)
    const updateRuleAction: UpdateEmployeeAction = {
        type: EmployeeActionTypes.UPDATE_EMPLOYEE,
        employee,
    }
    dispatch(updateRuleAction)
    dispatch(refetchUserRelatedTables())
    return employee
}
